import React, { useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";

import type { GoalBasedFinancialPlanId } from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useDeleteGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useDeleteGoalBasedFinancialPlan";
import useGetGoalBasedFinancialPlans from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlans";
import usePatchGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/usePatchGoalBasedFinancialPlan";
import type { GoalBasedFinancialPlanResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { GoalBasedFinancialPlanStatus } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import DropdownMenu from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Dropdown/Dropdown";
import MenuActionItem from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Dropdown/MenuItem/MenuActionItem";
import type { Item } from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Dropdown/MenuSection/MenuSection";
import DropdownMenuSection from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Dropdown/MenuSection/MenuSection";
import ManageFinancialPlansModal from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Modal/Container";
import DeleteFinancialPlanModal from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Modal/Delete/DeleteItemModal";
import FinancialPlanDetailsModal from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Modal/ItemDetailsModal/ItemDetailsModal";
import ModalSectionWithItems from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Modal/ModalSectionWithItems/ModalSection";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import TextWithPlusButton from "@src/sharedComponents/TextWithPlusButton/TextWithPlusButton";

interface Props {
  goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  setGoalBasedFinancialPlanId: (id: GoalBasedFinancialPlanId) => void;
  showCreateFinancialPlanModal: () => void;
  onSelect: (id: GoalBasedFinancialPlanId) => void;
  width?: number;
}

type TransformedFinancialPlan = Item<GoalBasedFinancialPlanId>;

const FinancialPlansDropdownMenu: React.FC<Props> = ({
  goalBasedFinancialPlanId,
  setGoalBasedFinancialPlanId,
  showCreateFinancialPlanModal,
  onSelect,
  width = 360,
}: Props) => {
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();
  const [localGoalBasedFinancialPlanId, setLocalGoalBasedFinancialPlanId] =
    useState<GoalBasedFinancialPlanId>(goalBasedFinancialPlanId);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showFinancialPlanDetailsModal, setShowFinancialPlanDetailsModal] =
    useState<boolean>(false);
  const [showManageFinancialPlansModal, setShowManageFinancialPlansModal] =
    useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [textInput, setTextInput] = useState<string>("");
  const [isFavoriteCheckboxChecked, setIsFavoriteCheckbox] =
    useState<boolean>(false);

  const notify = useStatusNotification();

  const auth = useRequestAuth();

  const { data: goalBasedFinancialPlans } = useGetGoalBasedFinancialPlans(
    clientId,
    auth
  );

  const {
    mutateAsync: deleteGoalBasedFinancialPlan,
    isLoading: isDeletingFinancialPlan,
  } = useDeleteGoalBasedFinancialPlan(auth);

  const {
    mutateAsync: patchGoalBasedFinancialPlan,
    isLoading: isPatchingFinancialPlan,
  } = usePatchGoalBasedFinancialPlan(auth);

  const { plans, drafts } = useMemo(() => {
    if (goalBasedFinancialPlans === undefined) {
      return {
        plans: [],
        drafts: [],
      };
    }

    const { Incomplete, Complete } = GoalBasedFinancialPlanStatus.Enum;

    const transformGoalBasedFinancialPlan = (
      p: GoalBasedFinancialPlanResponse
    ): TransformedFinancialPlan => ({
      id: p.goalBasedFinancialPlanId,
      name: p.name,
      isFavorite: p.isFavorite,
    });

    const { plans, drafts } = goalBasedFinancialPlans.reduce(
      (acc, plan: GoalBasedFinancialPlanResponse) => {
        const transformedPlan = transformGoalBasedFinancialPlan(plan);

        if (plan.status === Incomplete) {
          acc.drafts.push(transformedPlan);
        } else if (plan.status === Complete) {
          acc.plans.push(transformedPlan);
        }

        return acc;
      },
      { plans: [], drafts: [] } as {
        plans: TransformedFinancialPlan[];
        drafts: TransformedFinancialPlan[];
      }
    );

    return {
      plans: orderBy(plans, ["name"], "asc"),
      drafts: orderBy(drafts, ["name"], "asc"),
    };
  }, [goalBasedFinancialPlans]);

  const selectedGoalBasedFinancialPlan = useMemo(() => {
    if (goalBasedFinancialPlans === undefined) {
      return undefined;
    }

    return goalBasedFinancialPlans.find(
      (p) => p.goalBasedFinancialPlanId === goalBasedFinancialPlanId
    );
  }, [goalBasedFinancialPlanId, goalBasedFinancialPlans]);

  const onSetFavorite = async (id: GoalBasedFinancialPlanId) => {
    const body = {
      isFavorite: true,
    };

    try {
      await patchGoalBasedFinancialPlan({
        goalBasedFinancialPlanId: id,
        body,
      });
      notify("Financial plan favorited", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error creating plan ${message ?? ""}`, "Error");
      } else {
        notify(
          "There was an error in favoriting a plan. Please try again.",
          "Error"
        );
      }
    }
  };

  const onSave = async () => {
    const body = {
      isFavorite: isFavoriteCheckboxChecked,
      name: textInput,
    };

    try {
      await patchGoalBasedFinancialPlan({
        goalBasedFinancialPlanId: localGoalBasedFinancialPlanId,
        body,
      });
      notify("Financial plan updated", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error creating plan ${message ?? ""}`, "Error");
      } else {
        notify(
          "There was an error in creating a plan. Please try again.",
          "Error"
        );
      }
    }
  };

  const onDelete = async () => {
    await deleteGoalBasedFinancialPlan({
      goalBasedFinancialPlanId: localGoalBasedFinancialPlanId,
    });

    notify("Financial plan deleted", "Success");

    setShowDeleteModal(false);
    setShowFinancialPlanDetailsModal(false);

    if (goalBasedFinancialPlans?.length) {
      const favoritePlan = goalBasedFinancialPlans.find((p) => p.isFavorite);

      if (favoritePlan) {
        setLocalGoalBasedFinancialPlanId(favoritePlan.goalBasedFinancialPlanId);
        setGoalBasedFinancialPlanId(favoritePlan.goalBasedFinancialPlanId);
      }
    }
  };

  const onHandleManageFinancialPlansModalItemClick = (
    id: GoalBasedFinancialPlanId
  ) => {
    const proj = (goalBasedFinancialPlans ?? []).find(
      (p) => p.goalBasedFinancialPlanId === id
    );
    if (proj === undefined) {
      throw new Error(`Financial plan with id: ${id} not found`);
    }

    setTextInput(proj.name);
    setIsFavoriteCheckbox(proj.isFavorite);
    setLocalGoalBasedFinancialPlanId(id);
    setShowFinancialPlanDetailsModal(true);
  };

  const isLoading = isDeletingFinancialPlan || isPatchingFinancialPlan;

  const handleShowManageFinancialPlansModal = () =>
    setShowManageFinancialPlansModal(true);

  const handleSetHideManageFinancialPlansModal = () =>
    setShowManageFinancialPlansModal(false);

  const handleOnClickPlanSettings = () => {
    setShowDropdown(false);
    handleShowManageFinancialPlansModal();
  };

  const handleOnClickAddPlan = () => {
    setShowDropdown(false);
    showCreateFinancialPlanModal();
  };

  const handleOnShowDeleteModal = () => setShowDeleteModal(true);

  const handleOnHideDeleteModal = () => setShowDeleteModal(false);

  const setIdAndCloseMenu = (id: GoalBasedFinancialPlanId) => {
    setLocalGoalBasedFinancialPlanId(id);
    setGoalBasedFinancialPlanId(id);
    setShowDropdown(false);
    onSelect(id);
  };

  const setHideFinancialPlansDetailsModal = () =>
    setShowFinancialPlanDetailsModal(false);

  return (
    <>
      {isLoading && <LogoLoadingStill onTop />}

      {showManageFinancialPlansModal && (
        <ManageFinancialPlansModal
          title={"Manage Financial Plans"}
          closeModal={handleSetHideManageFinancialPlansModal}
        >
          <>
            <ModalSectionWithItems
              header={"Plans"}
              items={plans}
              onClickItem={onHandleManageFinancialPlansModalItemClick}
              isLoading={isLoading}
              setFavorite={onSetFavorite}
            />

            <ModalSectionWithItems
              header={"Drafts"}
              items={drafts}
              onClickItem={onHandleManageFinancialPlansModalItemClick}
              isLoading={isLoading}
              setFavorite={onSetFavorite}
              headerEnd={
                <TextWithPlusButton
                  text={"Create Plan"}
                  onClick={showCreateFinancialPlanModal}
                />
              }
            />
          </>
        </ManageFinancialPlansModal>
      )}

      {showFinancialPlanDetailsModal && (
        <FinancialPlanDetailsModal
          title={"Plan Details"}
          textInputLabel={"Plan Name"}
          textInputValue={textInput}
          isChecked={isFavoriteCheckboxChecked}
          checkboxLabel={"Mark as favorite"}
          checkboxSubtext={
            "Your favorite is the first plan you see on this page."
          }
          goBack={setHideFinancialPlansDetailsModal}
          onSave={onSave}
          deleteButtonText={"Delete Plan"}
          showDeleteModal={handleOnShowDeleteModal}
          setTextInput={setTextInput}
          setCheckbox={setIsFavoriteCheckbox}
          isLoading={false}
          isDisabled={false}
        />
      )}

      {showDeleteModal && (
        <DeleteFinancialPlanModal
          title={"Are you sure you want to delete this plan?"}
          text={"Deleting this plan will remove it from your plans."}
          deleteButtonText={"Delete Plan"}
          onDelete={onDelete}
          onClose={handleOnHideDeleteModal}
        />
      )}

      <DropdownMenu
        width={width}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        isLoading={isLoading}
        isDisabled={isLoading}
        curItemName={selectedGoalBasedFinancialPlan?.name ?? ""}
        menuActionItems={
          <>
            <MenuActionItem
              text={"New plan"}
              icon={"Add"}
              onClick={handleOnClickAddPlan}
            />

            <MenuActionItem
              text={"Manage plans"}
              icon={"Gear"}
              onClick={handleOnClickPlanSettings}
            />
          </>
        }
      >
        <>
          <DropdownMenuSection
            currentId={goalBasedFinancialPlanId}
            setId={setIdAndCloseMenu}
            header={"Plans"}
            items={plans}
            isLoading={isLoading}
          />

          <DropdownMenuSection
            currentId={goalBasedFinancialPlanId}
            setId={setIdAndCloseMenu}
            header={"Drafts"}
            items={drafts}
            isLoading={isLoading}
          />
        </>
      </DropdownMenu>
    </>
  );
};

export default FinancialPlansDropdownMenu;
